import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import axios from "axios";
import { 
  Phone, Mail, MessageSquare, DollarSign, Clock, CheckCircle, 
  XCircle, ArrowRight, Plus, X, Calendar, TrendingUp, Users,
  ChevronDown, ChevronUp, Send, Bell, Filter, MoreVertical
} from "lucide-react";

const PIPELINE_STAGES = [
  { id: "new", label: "New Lead", color: "bg-blue-500" },
  { id: "contacted", label: "Contacted", color: "bg-purple-500" },
  { id: "estimate_given", label: "Estimate Given", color: "bg-yellow-500" },
  { id: "scheduled", label: "Scheduled", color: "bg-orange-500" },
  { id: "completed", label: "Completed", color: "bg-green-500" },
  { id: "closed", label: "Closed", color: "bg-gray-500" }
];

const SalesCRMPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [viewMode, setViewMode] = useState("pipeline"); // pipeline or list
  const [showEstimateModal, setShowEstimateModal] = useState(false);
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  
  // Form states
  const [estimateForm, setEstimateForm] = useState({ amount: "", description: "", notes: "" });
  const [followUpDate, setFollowUpDate] = useState("");
  const [newLeadForm, setNewLeadForm] = useState({
    name: "", phone: "", email: "", service: "", notes: "", location: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { withCredentials: true }),
        axios.get(`${API}/leads/stats`, { withCredentials: true })
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status updated to ${newStatus.replace("_", " ")}`);
      fetchData();
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleSendEstimate = async () => {
    if (!estimateForm.amount || !estimateForm.description) {
      toast.error("Please fill in amount and description");
      return;
    }
    try {
      await axios.post(`${API}/leads/${selectedLead.id}/estimate`, {
        lead_id: selectedLead.id,
        amount: parseFloat(estimateForm.amount),
        description: estimateForm.description,
        notes: estimateForm.notes
      }, { withCredentials: true });
      toast.success("Estimate sent!");
      setShowEstimateModal(false);
      setEstimateForm({ amount: "", description: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to send estimate");
    }
  };

  const handleSetFollowUp = async () => {
    if (!followUpDate) {
      toast.error("Please select a date");
      return;
    }
    try {
      await axios.patch(`${API}/leads/${selectedLead.id}`, {
        follow_up_date: followUpDate
      }, { withCredentials: true });
      toast.success("Follow-up scheduled!");
      setShowFollowUpModal(false);
      setFollowUpDate("");
      fetchData();
    } catch (error) {
      toast.error("Failed to schedule follow-up");
    }
  };

  const handleAddLead = async () => {
    if (!newLeadForm.name || !newLeadForm.phone || !newLeadForm.service) {
      toast.error("Please fill in name, phone, and service");
      return;
    }
    try {
      await axios.post(`${API}/leads`, {
        ...newLeadForm,
        source: "manual"
      }, { withCredentials: true });
      toast.success("Lead added!");
      setShowAddLeadModal(false);
      setNewLeadForm({ name: "", phone: "", email: "", service: "", notes: "", location: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to add lead");
    }
  };

  const getLeadsByStatus = (status) => {
    return leads.filter(lead => lead.status === status);
  };

  const filteredLeads = filterStatus === "all" 
    ? leads 
    : leads.filter(l => l.status === filterStatus);

  const services = [
    "Drywall", "Interior Paint", "Exterior Paint", "Flooring", "Tile",
    "Door Installation", "Window Installation", "Roof Repair", "Cabinet Installation",
    "Stucco", "Siding", "Minor Electrical", "Other"
  ];

  if (loading) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#FF6B35]"></div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10]" data-testid="sales-crm-page">
      {/* Header */}
      <div className="bg-[#15161C] border-b border-[#2A2B32]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white">Sales CRM</h1>
              <p className="text-[#A0AAB2] text-sm">Manage your leads and close deals</p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setViewMode(viewMode === "pipeline" ? "list" : "pipeline")}
                className="px-4 py-2 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm hover:border-[#FF6B35] transition-all"
              >
                {viewMode === "pipeline" ? "List View" : "Pipeline View"}
              </button>
              <button
                onClick={() => setShowAddLeadModal(true)}
                className="flex items-center space-x-2 px-4 py-2 gradient-btn rounded-lg text-sm font-semibold"
                data-testid="add-lead-btn"
              >
                <Plus size={18} />
                <span>Add Lead</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-1">
                <Users size={16} className="text-blue-400" />
                <span className="text-[#A0AAB2] text-xs">Total</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.total_leads}</p>
            </div>
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-1">
                <TrendingUp size={16} className="text-green-400" />
                <span className="text-[#A0AAB2] text-xs">Close Rate</span>
              </div>
              <p className="text-2xl font-bold text-green-400">{stats.close_rate}%</p>
            </div>
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-1">
                <Bell size={16} className="text-yellow-400" />
                <span className="text-[#A0AAB2] text-xs">Follow-ups</span>
              </div>
              <p className="text-2xl font-bold text-yellow-400">{stats.follow_ups_due}</p>
            </div>
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                <span className="text-[#A0AAB2] text-xs">New</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.new}</p>
            </div>
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                <span className="text-[#A0AAB2] text-xs">Estimates</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.estimate_given}</p>
            </div>
            <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-4">
              <div className="flex items-center space-x-2 mb-1">
                <CheckCircle size={16} className="text-green-400" />
                <span className="text-[#A0AAB2] text-xs">Completed</span>
              </div>
              <p className="text-2xl font-bold text-white">{stats.completed}</p>
            </div>
          </div>
        )}

        {/* Pipeline View */}
        {viewMode === "pipeline" && (
          <div className="overflow-x-auto pb-4">
            <div className="flex space-x-4 min-w-max">
              {PIPELINE_STAGES.map((stage) => {
                const stageLeads = getLeadsByStatus(stage.id);
                return (
                  <div key={stage.id} className="w-72 flex-shrink-0">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className={`w-3 h-3 rounded-full ${stage.color}`}></span>
                      <span className="text-white font-semibold text-sm">{stage.label}</span>
                      <span className="bg-[#2A2B32] text-[#A0AAB2] text-xs px-2 py-0.5 rounded-full">
                        {stageLeads.length}
                      </span>
                    </div>
                    <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                      {stageLeads.map((lead) => (
                        <div
                          key={lead.id}
                          onClick={() => setSelectedLead(lead)}
                          className="bg-[#15161C] border border-[#2A2B32] rounded-lg p-4 cursor-pointer hover:border-[#FF6B35]/50 transition-all"
                          data-testid={`lead-card-${lead.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-white font-semibold text-sm">{lead.name}</h3>
                            {lead.follow_up_date && (
                              <Bell size={14} className="text-yellow-400" />
                            )}
                          </div>
                          <p className="text-[#FF6B35] text-xs mb-1">{lead.service}</p>
                          <p className="text-[#A0AAB2] text-xs">{lead.phone}</p>
                          {lead.estimate_amount && (
                            <p className="text-green-400 text-sm font-semibold mt-2">
                              ${lead.estimate_amount.toLocaleString()}
                            </p>
                          )}
                        </div>
                      ))}
                      {stageLeads.length === 0 && (
                        <div className="bg-[#15161C]/50 border border-dashed border-[#2A2B32] rounded-lg p-4 text-center">
                          <p className="text-[#A0AAB2] text-xs">No leads</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* List View */}
        {viewMode === "list" && (
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl overflow-hidden">
            {/* Filter */}
            <div className="p-4 border-b border-[#2A2B32] flex items-center space-x-3">
              <Filter size={16} className="text-[#A0AAB2]" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-[#1F2027] border border-[#2A2B32] rounded-lg px-3 py-1.5 text-white text-sm"
              >
                <option value="all">All Leads</option>
                {PIPELINE_STAGES.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>
            
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1F2027]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#A0AAB2]">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#A0AAB2]">Service</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#A0AAB2]">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#A0AAB2]">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#A0AAB2]">Estimate</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-[#A0AAB2]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLeads.map((lead) => (
                    <tr 
                      key={lead.id} 
                      className="border-t border-[#2A2B32] hover:bg-[#1F2027] cursor-pointer"
                      onClick={() => setSelectedLead(lead)}
                    >
                      <td className="px-4 py-3">
                        <p className="text-white text-sm font-medium">{lead.name}</p>
                        <p className="text-[#A0AAB2] text-xs">{lead.email}</p>
                      </td>
                      <td className="px-4 py-3 text-[#FF6B35] text-sm">{lead.service}</td>
                      <td className="px-4 py-3 text-white text-sm">{lead.phone}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          PIPELINE_STAGES.find(s => s.id === lead.status)?.color
                        } bg-opacity-20 text-white`}>
                          {PIPELINE_STAGES.find(s => s.id === lead.status)?.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-green-400 text-sm font-medium">
                        {lead.estimate_amount ? `$${lead.estimate_amount.toLocaleString()}` : "-"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-2">
                          <a href={`tel:${lead.phone}`} className="p-1.5 bg-[#2A2B32] rounded hover:bg-[#3A3B42]">
                            <Phone size={14} className="text-green-400" />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Lead Detail Sidebar */}
      {selectedLead && (
        <div className="fixed inset-y-0 right-0 w-full md:w-96 bg-[#15161C] border-l border-[#2A2B32] z-50 overflow-y-auto" data-testid="lead-detail">
          <div className="sticky top-0 bg-[#15161C] border-b border-[#2A2B32] p-4 flex items-center justify-between">
            <h2 className="text-lg font-bold text-white">Lead Details</h2>
            <button 
              onClick={() => setSelectedLead(null)}
              className="p-2 rounded-lg hover:bg-white/10"
            >
              <X size={20} className="text-[#A0AAB2]" />
            </button>
          </div>
          
          <div className="p-4 space-y-6">
            {/* Contact Info */}
            <div>
              <h3 className="text-white font-semibold text-lg mb-1">{selectedLead.name}</h3>
              <p className="text-[#FF6B35] text-sm mb-3">{selectedLead.service}</p>
              
              <div className="space-y-2">
                <a href={`tel:${selectedLead.phone}`} className="flex items-center space-x-3 p-3 bg-[#1F2027] rounded-lg hover:bg-[#2A2B32] transition-colors">
                  <Phone size={18} className="text-green-400" />
                  <span className="text-white text-sm">{selectedLead.phone}</span>
                </a>
                {selectedLead.email && (
                  <a href={`mailto:${selectedLead.email}`} className="flex items-center space-x-3 p-3 bg-[#1F2027] rounded-lg hover:bg-[#2A2B32] transition-colors">
                    <Mail size={18} className="text-blue-400" />
                    <span className="text-white text-sm">{selectedLead.email}</span>
                  </a>
                )}
                <div className="flex items-center space-x-3 p-3 bg-[#1F2027] rounded-lg opacity-50">
                  <MessageSquare size={18} className="text-purple-400" />
                  <span className="text-[#A0AAB2] text-sm">Text (Coming Soon)</span>
                </div>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="text-[#A0AAB2] text-xs font-medium mb-2 block">Status</label>
              <select
                value={selectedLead.status}
                onChange={(e) => handleStatusChange(selectedLead.id, e.target.value)}
                className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
              >
                {PIPELINE_STAGES.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Estimate */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[#A0AAB2] text-xs font-medium">Estimate</label>
                <button
                  onClick={() => setShowEstimateModal(true)}
                  className="text-[#FF6B35] text-xs hover:underline"
                >
                  {selectedLead.estimate_amount ? "Update" : "+ Add Estimate"}
                </button>
              </div>
              {selectedLead.estimate_amount ? (
                <div className="bg-[#1F2027] border border-green-500/30 rounded-lg p-4">
                  <p className="text-green-400 text-2xl font-bold">${selectedLead.estimate_amount.toLocaleString()}</p>
                  {selectedLead.estimate_notes && (
                    <p className="text-[#A0AAB2] text-sm mt-2">{selectedLead.estimate_notes}</p>
                  )}
                  {selectedLead.estimate_sent && (
                    <p className="text-green-400 text-xs mt-2 flex items-center space-x-1">
                      <CheckCircle size={12} />
                      <span>Sent</span>
                    </p>
                  )}
                </div>
              ) : (
                <div className="bg-[#1F2027] border border-dashed border-[#2A2B32] rounded-lg p-4 text-center">
                  <p className="text-[#A0AAB2] text-sm">No estimate yet</p>
                </div>
              )}
            </div>

            {/* Follow-up */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-[#A0AAB2] text-xs font-medium">Follow-up</label>
                <button
                  onClick={() => setShowFollowUpModal(true)}
                  className="text-[#FF6B35] text-xs hover:underline"
                >
                  {selectedLead.follow_up_date ? "Change" : "+ Set Reminder"}
                </button>
              </div>
              {selectedLead.follow_up_date ? (
                <div className="bg-[#1F2027] border border-yellow-500/30 rounded-lg p-3 flex items-center space-x-3">
                  <Calendar size={18} className="text-yellow-400" />
                  <span className="text-white text-sm">{selectedLead.follow_up_date}</span>
                </div>
              ) : (
                <div className="bg-[#1F2027] border border-dashed border-[#2A2B32] rounded-lg p-3 text-center">
                  <p className="text-[#A0AAB2] text-sm">No follow-up set</p>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="text-[#A0AAB2] text-xs font-medium mb-2 block">Notes</label>
              <div className="bg-[#1F2027] border border-[#2A2B32] rounded-lg p-3">
                <p className="text-white text-sm">{selectedLead.notes || "No notes"}</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleStatusChange(selectedLead.id, "scheduled")}
                className="flex items-center justify-center space-x-2 bg-orange-500/20 text-orange-400 py-3 rounded-lg hover:bg-orange-500/30 transition-colors"
              >
                <Calendar size={18} />
                <span className="text-sm font-medium">Schedule</span>
              </button>
              <button
                onClick={() => handleStatusChange(selectedLead.id, "completed")}
                className="flex items-center justify-center space-x-2 bg-green-500/20 text-green-400 py-3 rounded-lg hover:bg-green-500/30 transition-colors"
              >
                <CheckCircle size={18} />
                <span className="text-sm font-medium">Complete</span>
              </button>
            </div>

            {/* Activity Log */}
            {selectedLead.activity_log && selectedLead.activity_log.length > 0 && (
              <div>
                <label className="text-[#A0AAB2] text-xs font-medium mb-2 block">Activity</label>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {selectedLead.activity_log.slice().reverse().map((activity, idx) => (
                    <div key={idx} className="flex items-start space-x-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-[#FF6B35] mt-1.5"></div>
                      <div>
                        <p className="text-white">{activity.details}</p>
                        <p className="text-[#A0AAB2] text-xs">
                          {new Date(activity.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Estimate Modal */}
      {showEstimateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-white mb-4">Send Estimate</h3>
            <div className="space-y-4">
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Amount ($)</label>
                <input
                  type="number"
                  value={estimateForm.amount}
                  onChange={(e) => setEstimateForm(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                  placeholder="500"
                />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Description</label>
                <textarea
                  value={estimateForm.description}
                  onChange={(e) => setEstimateForm(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white resize-none"
                  rows={3}
                  placeholder="Drywall repair - medium patch in living room..."
                />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Notes (optional)</label>
                <textarea
                  value={estimateForm.notes}
                  onChange={(e) => setEstimateForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white resize-none"
                  rows={2}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowEstimateModal(false)}
                className="flex-1 py-3 bg-[#2A2B32] text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSendEstimate}
                className="flex-1 py-3 gradient-btn rounded-lg font-semibold flex items-center justify-center space-x-2"
              >
                <Send size={18} />
                <span>Send Estimate</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Follow-up Modal */}
      {showFollowUpModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-white mb-4">Set Follow-up</h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    setFollowUpDate(tomorrow.toISOString().split('T')[0]);
                  }}
                  className="py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm hover:border-[#FF6B35]"
                >
                  24 Hours
                </button>
                <button
                  onClick={() => {
                    const threeDays = new Date();
                    threeDays.setDate(threeDays.getDate() + 3);
                    setFollowUpDate(threeDays.toISOString().split('T')[0]);
                  }}
                  className="py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm hover:border-[#FF6B35]"
                >
                  3 Days
                </button>
                <button
                  onClick={() => {
                    const week = new Date();
                    week.setDate(week.getDate() + 7);
                    setFollowUpDate(week.toISOString().split('T')[0]);
                  }}
                  className="py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm hover:border-[#FF6B35]"
                >
                  1 Week
                </button>
                <button
                  onClick={() => {
                    const twoWeeks = new Date();
                    twoWeeks.setDate(twoWeeks.getDate() + 14);
                    setFollowUpDate(twoWeeks.toISOString().split('T')[0]);
                  }}
                  className="py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white text-sm hover:border-[#FF6B35]"
                >
                  2 Weeks
                </button>
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Or select date</label>
                <input
                  type="date"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowFollowUpModal(false)}
                className="flex-1 py-3 bg-[#2A2B32] text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleSetFollowUp}
                className="flex-1 py-3 gradient-btn rounded-lg font-semibold"
              >
                Set Reminder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl w-full max-w-md p-6 my-8">
            <h3 className="text-lg font-bold text-white mb-4">Add New Lead</h3>
            <div className="space-y-4">
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Name *</label>
                <input
                  type="text"
                  value={newLeadForm.name}
                  onChange={(e) => setNewLeadForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Phone *</label>
                <input
                  type="tel"
                  value={newLeadForm.phone}
                  onChange={(e) => setNewLeadForm(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                  placeholder="(555) 123-4567"
                />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Email</label>
                <input
                  type="email"
                  value={newLeadForm.email}
                  onChange={(e) => setNewLeadForm(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                  placeholder="john@example.com"
                />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Service *</label>
                <select
                  value={newLeadForm.service}
                  onChange={(e) => setNewLeadForm(prev => ({ ...prev, service: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                >
                  <option value="">Select service</option>
                  {services.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Location</label>
                <input
                  type="text"
                  value={newLeadForm.location}
                  onChange={(e) => setNewLeadForm(prev => ({ ...prev, location: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white"
                  placeholder="Lakeland, FL"
                />
              </div>
              <div>
                <label className="text-[#A0AAB2] text-sm mb-1 block">Notes</label>
                <textarea
                  value={newLeadForm.notes}
                  onChange={(e) => setNewLeadForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full bg-[#1F2027] border border-[#2A2B32] rounded-lg px-4 py-3 text-white resize-none"
                  rows={2}
                  placeholder="Any additional notes..."
                />
              </div>
            </div>
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowAddLeadModal(false)}
                className="flex-1 py-3 bg-[#2A2B32] text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleAddLead}
                className="flex-1 py-3 gradient-btn rounded-lg font-semibold"
              >
                Add Lead
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesCRMPage;
